import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CheckIcon, ArrowUpTrayIcon, ScissorsIcon } from '@heroicons/react/24/outline';
import { AspectRatio, AspectRatioLabels, getLabelsByLocale, Locale } from '@packages/global';

interface ImageCropperProps {
  /** 原始图片 URL */
  imageUrl: string;
  /** 锁定比例模式：传入则固定该比例，不传则自由模式 */
  aspectRatio?: AspectRatio;
  /** 确认回调，返回上传后的新 URL */
  onConfirm: (url: string) => void;
  /** 取消回调 */
  onCancel: () => void;
}

interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// 比例值映射
const RATIO_VALUES: Record<AspectRatio, number> = {
  [AspectRatio.SQUARE]: 1,
  [AspectRatio.LANDSCAPE]: 16 / 9,
  [AspectRatio.PORTRAIT]: 9 / 16,
  [AspectRatio.STANDARD]: 4 / 3,
  [AspectRatio.PORTRAIT_STANDARD]: 3 / 4,
};

type Stage = 'idle' | 'uploading' | 'done' | 'error';

export default function ImageCropper({ imageUrl, aspectRatio, onConfirm, onCancel }: ImageCropperProps) {
  const { t, i18n } = useTranslation(['common_ui', 'common']);
  const isLocked = aspectRatio !== undefined;
  const arLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);

  // 自由模式快速比例选项（含"不限制"）
  const FREE_RATIOS: Array<{ label: string; value: AspectRatio | null }> = [
    { label: t('common_ui:cropper.ratioFree'), value: null },
    { label: '1:1', value: AspectRatio.SQUARE },
    { label: '16:9', value: AspectRatio.LANDSCAPE },
    { label: '9:16', value: AspectRatio.PORTRAIT },
    { label: '4:3', value: AspectRatio.STANDARD },
    { label: '3:4', value: AspectRatio.PORTRAIT_STANDARD },
  ];

  // 自由模式下当前选中的比例（null = 不限制）
  const [freeRatio, setFreeRatio] = useState<AspectRatio | null>(null);

  // 实际生效的比例值（null = 不限制）
  const activeRatioValue = isLocked
    ? RATIO_VALUES[aspectRatio!]
    : freeRatio !== null ? RATIO_VALUES[freeRatio] : null;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // 图片在 canvas 上的实际绘制区域（相对于 canvas 坐标）
  const imgDrawRef = useRef<{ x: number; y: number; w: number; h: number }>({ x: 0, y: 0, w: 0, h: 0 });

  // 裁剪框（相对于 canvas 坐标）
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, w: 0, h: 0 });
  const cropRef = useRef<CropRect>({ x: 0, y: 0, w: 0, h: 0 });

  const [imgLoaded, setImgLoaded] = useState(false);
  const [stage, setStage] = useState<Stage>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // 拖拽状态
  const dragRef = useRef<{
    type: 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | 'new' | null;
    startX: number;
    startY: number;
    origCrop: CropRect;
  }>({ type: null, startX: 0, startY: 0, origCrop: { x: 0, y: 0, w: 0, h: 0 } });

  // 加载图片
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // 绘制 canvas
  const draw = useCallback((c: CropRect) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x: ix, y: iy, w: iw, h: ih } = imgDrawRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 绘制图片
    ctx.drawImage(img, ix, iy, iw, ih);

    // 遮罩
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 裁剪区域清除遮罩
    if (c.w > 0 && c.h > 0) {
      ctx.clearRect(c.x, c.y, c.w, c.h);
      ctx.drawImage(img, ix, iy, iw, ih);
      // 重新绘制裁剪区域内的图片（清除遮罩后重绘）
      ctx.save();
      ctx.beginPath();
      ctx.rect(c.x, c.y, c.w, c.h);
      ctx.clip();
      ctx.drawImage(img, ix, iy, iw, ih);
      ctx.restore();

      // 裁剪框边框
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 2;
      ctx.strokeRect(c.x, c.y, c.w, c.h);

      // 三等分辅助线
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(c.x + (c.w / 3) * i, c.y);
        ctx.lineTo(c.x + (c.w / 3) * i, c.y + c.h);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(c.x, c.y + (c.h / 3) * i);
        ctx.lineTo(c.x + c.w, c.y + (c.h / 3) * i);
        ctx.stroke();
      }

      // 四角手柄
      const hs = 8;
      ctx.fillStyle = '#6366f1';
      const corners = [
        [c.x, c.y], [c.x + c.w - hs, c.y],
        [c.x, c.y + c.h - hs], [c.x + c.w - hs, c.y + c.h - hs],
      ];
      corners.forEach(([cx, cy]) => ctx.fillRect(cx, cy, hs, hs));
    }
  }, []);

  // canvas 显示尺寸 state（用于 style 注入）
  const [canvasStyle, setCanvasStyle] = useState<{ width: number; height: number } | null>(null);
  // 面板宽度跟随 canvas 动态调整
  const [panelWidth, setPanelWidth] = useState<number | null>(null);

  // 初始化 canvas 尺寸 + 裁剪框
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const PANEL_PADDING = 40; // p-5 两侧 = 20*2
    const FIXED_UI_H = 180;   // 标题+比例栏+按钮+gap+padding 合计
    // 视口可用区域（留出外层 p-4 = 16px*2）
    const vpW = window.innerWidth - 32;
    const vpH = window.innerHeight * 0.9;

    // canvas 最大可用空间
    const maxW = vpW - PANEL_PADDING;
    const maxH = vpH - FIXED_UI_H;

    const imgRatio = img.naturalWidth / img.naturalHeight;
    let cw = maxW;
    let ch = cw / imgRatio;
    if (ch > maxH) { ch = maxH; cw = ch * imgRatio; }

    canvas.width = Math.floor(cw);
    canvas.height = Math.floor(ch);
    setCanvasStyle({ width: canvas.width, height: canvas.height });
    // 面板宽度 = canvas 宽 + padding，但不超过视口
    setPanelWidth(Math.min(canvas.width + PANEL_PADDING, vpW));

    imgDrawRef.current = { x: 0, y: 0, w: canvas.width, h: canvas.height };

    // 初始裁剪框：全图或按比例居中
    let initCrop: CropRect;
    if (activeRatioValue !== null) {
      const ratio = activeRatioValue;
      let w = canvas.width * 0.8;
      let h = w / ratio;
      if (h > canvas.height * 0.8) { h = canvas.height * 0.8; w = h * ratio; }
      initCrop = {
        x: (canvas.width - w) / 2,
        y: (canvas.height - h) / 2,
        w, h,
      };
    } else {
      initCrop = { x: 0, y: 0, w: canvas.width, h: canvas.height };
    }

    cropRef.current = initCrop;
    setCrop(initCrop);
    draw(initCrop);
  }, [activeRatioValue, draw]);

  useEffect(() => {
    if (imgLoaded) initCanvas();
  }, [imgLoaded, initCanvas]);

  // 比例切换时重置裁剪框
  useEffect(() => {
    if (imgLoaded) initCanvas();
  }, [freeRatio, imgLoaded, initCanvas]);

  // 鼠标坐标转 canvas 坐标
  const getCanvasPos = (e: React.MouseEvent | MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // 判断点击位置（手柄 / 内部移动 / 新建）
  const getHitType = (pos: { x: number; y: number }, c: CropRect) => {
    const hs = 12;
    const { x, y, w, h } = c;
    if (Math.abs(pos.x - x) < hs && Math.abs(pos.y - y) < hs) return 'resize-tl';
    if (Math.abs(pos.x - (x + w)) < hs && Math.abs(pos.y - y) < hs) return 'resize-tr';
    if (Math.abs(pos.x - x) < hs && Math.abs(pos.y - (y + h)) < hs) return 'resize-bl';
    if (Math.abs(pos.x - (x + w)) < hs && Math.abs(pos.y - (y + h)) < hs) return 'resize-br';
    if (pos.x > x && pos.x < x + w && pos.y > y && pos.y < y + h) return 'move';
    return 'new';
  };

  const clampCrop = (c: CropRect, canvas: HTMLCanvasElement): CropRect => {
    const { x: ix, y: iy, w: iw, h: ih } = imgDrawRef.current;
    const minX = ix, minY = iy, maxX = ix + iw, maxY = iy + ih;
    let { x, y, w, h } = c;
    x = Math.max(minX, Math.min(x, maxX - 4));
    y = Math.max(minY, Math.min(y, maxY - 4));
    w = Math.max(4, Math.min(w, maxX - x));
    h = Math.max(4, Math.min(h, maxY - y));
    void canvas;
    return { x, y, w, h };
  };

  const applyRatio = (c: CropRect, ratio: number | null, anchor: 'tl' | 'tr' | 'bl' | 'br' | 'center'): CropRect => {
    if (!ratio) return c;
    const { x: ix, y: iy, w: iw, h: ih } = imgDrawRef.current;
    let { x, y, w, h } = c;
    // 以宽度为基准调整高度
    h = w / ratio;
    if (h > ih) { h = ih; w = h * ratio; }
    if (w > iw) { w = iw; h = w / ratio; }
    // 根据锚点调整位置
    if (anchor === 'tr') x = c.x + c.w - w;
    else if (anchor === 'bl') y = c.y + c.h - h;
    else if (anchor === 'br') { x = c.x + c.w - w; y = c.y + c.h - h; }
    else if (anchor === 'center') { x = c.x + (c.w - w) / 2; y = c.y + (c.h - h) / 2; }
    x = Math.max(ix, Math.min(x, ix + iw - w));
    y = Math.max(iy, Math.min(y, iy + ih - h));
    return { x, y, w, h };
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (stage !== 'idle') return;
    const pos = getCanvasPos(e);
    const type = getHitType(pos, cropRef.current) as typeof dragRef.current.type;
    dragRef.current = { type, startX: pos.x, startY: pos.y, origCrop: { ...cropRef.current } };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const { type, startX, startY, origCrop } = dragRef.current;
      if (!type) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const pos = getCanvasPos(e);
      const dx = pos.x - startX;
      const dy = pos.y - startY;
      let newCrop: CropRect = { ...origCrop };

      if (type === 'move') {
        newCrop.x = origCrop.x + dx;
        newCrop.y = origCrop.y + dy;
      } else if (type === 'new') {
        const x = Math.min(startX, pos.x);
        const y = Math.min(startY, pos.y);
        const w = Math.abs(dx);
        const h = Math.abs(dy);
        newCrop = { x, y, w: Math.max(w, 4), h: Math.max(h, 4) };
      } else if (type === 'resize-tl') {
        newCrop.x = origCrop.x + dx;
        newCrop.y = origCrop.y + dy;
        newCrop.w = origCrop.w - dx;
        newCrop.h = origCrop.h - dy;
      } else if (type === 'resize-tr') {
        newCrop.y = origCrop.y + dy;
        newCrop.w = origCrop.w + dx;
        newCrop.h = origCrop.h - dy;
      } else if (type === 'resize-bl') {
        newCrop.x = origCrop.x + dx;
        newCrop.w = origCrop.w - dx;
        newCrop.h = origCrop.h + dy;
      } else if (type === 'resize-br') {
        newCrop.w = origCrop.w + dx;
        newCrop.h = origCrop.h + dy;
      }

      // 应用比例约束
      const anchor = type === 'resize-tl' ? 'br'
        : type === 'resize-tr' ? 'bl'
          : type === 'resize-bl' ? 'tr'
            : type === 'resize-br' ? 'tl'
              : 'center';
      if (activeRatioValue && type !== 'move') {
        newCrop = applyRatio(newCrop, activeRatioValue, anchor as any);
      }

      newCrop = clampCrop(newCrop, canvas);
      cropRef.current = newCrop;
      setCrop(newCrop);
      draw(newCrop);
    };

    const onUp = () => { dragRef.current.type = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [activeRatioValue, draw]);

  // 确认裁剪并上传
  const handleConfirm = async () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    if (crop.w < 4 || crop.h < 4) return;

    setStage('uploading');
    setErrorMsg('');

    try {
      // 将裁剪区域映射回原始图片坐标
      const { x: ix, y: iy, w: iw, h: ih } = imgDrawRef.current;
      const scaleX = img.naturalWidth / iw;
      const scaleY = img.naturalHeight / ih;
      const srcX = (crop.x - ix) * scaleX;
      const srcY = (crop.y - iy) * scaleY;
      const srcW = crop.w * scaleX;
      const srcH = crop.h * scaleY;

      // 用离屏 canvas 绘制裁剪结果
      const offscreen = document.createElement('canvas');
      offscreen.width = Math.round(srcW);
      offscreen.height = Math.round(srcH);
      const ctx = offscreen.getContext('2d');
      if (!ctx) throw new Error(t('common_ui:cropper.canvasError'));
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, offscreen.width, offscreen.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        offscreen.toBlob((b) => b ? resolve(b) : reject(new Error(t('common_ui:cropper.exportFailed'))), 'image/jpeg', 0.92);
      });

      const file = new File([blob], `cropped_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const fd = new FormData();
      fd.append('image', file);

      const res = await fetch('/api/user/upload/image', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const result = await res.json();
      if (!result.success || !result.data?.url) {
        throw new Error(result.message || t('common_ui:cropper.uploadFailed'));
      }

      setStage('done');
      onConfirm(result.data.url);
    } catch (err: any) {
      setErrorMsg(err.message || t('common_ui:cropper.processFailed'));
      setStage('error');
    }
  };

  const busy = stage === 'uploading';
  const canConfirm = crop.w >= 4 && crop.h >= 4 && !busy;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!busy ? onCancel : undefined} />

      {/* 面板 */}
      <div
        className="relative bg-bg-surface border border-border-subtle rounded-xl p-5 w-full shadow-2xl flex flex-col gap-4 overflow-y-auto"
        style={{ maxHeight: '90vh', width: panelWidth ? panelWidth : undefined, maxWidth: 'calc(100vw - 32px)' }}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-200 font-medium">
            <ScissorsIcon className="w-4 h-4 text-primary" />
            <span>{t('common_ui:cropper.title')}</span>
          </div>
          <button type="button" onClick={onCancel} disabled={busy} className="text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-40">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* 自由模式比例切换 */}
        {!isLocked && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">{t('common_ui:cropper.ratio')}</span>
            {FREE_RATIOS.map(({ label, value }) => (
              <button
                key={label}
                type="button"
                onClick={() => setFreeRatio(value)}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${freeRatio === value
                  ? 'bg-primary border-primary text-white'
                  : 'bg-bg-elevated border-border-default text-gray-400 hover:text-gray-200 hover:border-gray-500'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* 锁定模式比例提示 */}
        {isLocked && (
          <p className="text-xs text-gray-500">
            {t('common_ui:cropper.fixedRatio', { ratio: arLabels[aspectRatio!] })}
          </p>
        )}

        {/* Canvas 裁剪区域 */}
        <div ref={containerRef} className="w-full bg-bg-elevated rounded-lg flex items-center justify-center overflow-hidden">
          {!imgLoaded ? (
            <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2" />
              {t('common_ui:cropper.loadingImage')}
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              className="cursor-crosshair select-none block"
              onMouseDown={onMouseDown}
              style={canvasStyle
                ? { width: canvasStyle.width, height: canvasStyle.height }
                : { visibility: 'hidden', width: 0, height: 0 }}
            />
          )}
        </div>

        {/* 裁剪信息 */}
        {imgLoaded && crop.w >= 4 && (
          <p className="text-xs text-gray-500">
            {t('common_ui:cropper.selection', { width: Math.round(crop.w), height: Math.round(crop.h) })}
            {activeRatioValue && (
              <span className="ml-2 text-primary">
                ({isLocked ? arLabels[aspectRatio!] : arLabels[freeRatio!]})
              </span>
            )}
          </p>
        )}

        {/* 错误提示 */}
        {stage === 'error' && (
          <p className="text-xs text-red-400">{errorMsg}</p>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3 pt-1 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2 text-sm text-gray-400 hover:text-gray-200 bg-bg-elevated hover:bg-border-default rounded-lg transition-colors disabled:opacity-40"
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {busy ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {t('common_ui:cropper.uploading')}
              </>
            ) : stage === 'done' ? (
              <><CheckIcon className="w-4 h-4" />{t('common_ui:cropper.done')}</>
            ) : (
              <><ArrowUpTrayIcon className="w-4 h-4" />{t('common_ui:cropper.confirm')}</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
